import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useSalon } from "../context/SalonContext";
import api from "../lib/api";
import { returnHomeIfRequested } from "../lib/setupRoute";
import {
    AUDIENCE_PRESETS,
    TRIGGER_META,
    buildWhatsAppLink,
    computeEligibleClients,
    interpolateTemplate,
} from "../lib/marketing";

/**
 * Marketing — Hub de Automação do Kinng.app.
 *
 * 3 pílulas (mesmo padrão dos outros módulos):
 *   1) VISÃO GERAL — Dashboard de conversão + campanhas ativas
 *   2) RÉGUAS & TEMPLATES — Toggle das 5 réguas + editor de templates
 *   3) NOVA CAMPANHA — Wizard 3 cliques (Público → Template → Gatilho)
 */
const Marketing = () => {
    const { pendingWizardSeed, setPendingWizardSeed } = useSalon();
    const [tab, setTab] = useState(() => {
        const hint = sessionStorage.getItem("kinng.route.marketing.tab");
        if (hint) {
            sessionStorage.removeItem("kinng.route.marketing.tab");
            return hint;
        }
        return pendingWizardSeed ? "new" : "overview";
    });

    // When a seed arrives via context (e.g. from Clients suggestions), jump to wizard
    useEffect(() => {
        if (pendingWizardSeed) setTab("new");
    }, [pendingWizardSeed]);

    const TABS = [
        { id: "overview", label: "Visão Geral", icon: "fa-chart-pie" },
        { id: "rules", label: "Réguas & Templates", icon: "fa-cogs" },
        { id: "welcome", label: "Boas-vindas", icon: "fa-heart" },
        { id: "new", label: "Nova Campanha", icon: "fa-wand-magic-sparkles" },
    ];

    return (
        <div data-testid="marketing-module" className="space-y-5">
            {/* Welcome bar */}
            <header
                data-testid="marketing-welcome-bar"
                className="rounded-xl px-4 py-3 flex items-center justify-between gap-3 border border-amber-500/15 bg-black/40"
            >
                <div className="flex items-center gap-3 min-w-0">
                    <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-amber-500/30 to-amber-700/10 border border-amber-400/40 text-amber-300">
                        <i className="fa-solid fa-bullhorn"></i>
                    </div>
                    <div className="min-w-0">
                        <h1 className="salon-heading text-amber-100 text-lg leading-none">Hub de Marketing</h1>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-amber-300/60 mt-1">
                            Réguas inteligentes · WhatsApp · NPS · Reativação
                        </p>
                    </div>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-amber-200/40 hidden md:inline">
                    Conversão sem fricção em 3 cliques
                </span>
            </header>

            {/* Tab pills */}
            <div className="grid md:grid-cols-4 gap-3">
                {TABS.map((t) => {
                    const active = tab === t.id;
                    return (
                        <button
                            key={t.id}
                            data-testid={`marketing-tab-${t.id}`}
                            onClick={() => setTab(t.id)}
                            className={`gold-pill ${active ? "gold-pill-active" : ""} min-h-[60px] rounded-2xl px-4 flex items-center justify-center gap-3 text-sm md:text-base font-bold uppercase tracking-[0.15em]`}
                        >
                            <i className={`fa-solid ${t.icon}`}></i>
                            <span className="truncate">{t.label}</span>
                        </button>
                    );
                })}
            </div>

            {tab === "overview" && <OverviewTab />}
            {tab === "rules" && <RulesTab />}
            {tab === "welcome" && <WelcomeMessageTab />}
            {tab === "new" && (
                <CampaignWizard
                    seed={pendingWizardSeed}
                    onConsumeSeed={() => setPendingWizardSeed(null)}
                />
            )}
        </div>
    );
};

// ─── TAB · BOAS-VINDAS ───────────────────────────────────────────────────
const WelcomeMessageTab = () => {
    const { brand, updateBrand, clients, setActiveModule } = useSalon();
    const [message, setMessage] = useState(
        brand.welcomeMessage ||
            "Olá {{cliente_nome}}! 👑 Que alegria receber você no {{salao_nome}}. Estamos prontos para te atender com todo carinho. Qualquer coisa é só chamar por aqui."
    );
    const [saving, setSaving] = useState(false);

    const PREVIEW_TOKENS = {
        "{{cliente_nome}}": (clients[0]?.name || "Maria").split(" ")[0],
        "{{salao_nome}}": brand.shopName || "Kinng.app",
    };
    const preview = Object.entries(PREVIEW_TOKENS).reduce(
        (acc, [k, v]) => acc.split(k).join(v),
        message
    );

    const insertToken = (token) => setMessage((m) => m + " " + token);

    const handleSave = async () => {
        if (saving) return;
        const trimmed = message.trim();
        if (!trimmed) {
            toast.error("A mensagem não pode ficar vazia.");
            return;
        }
        setSaving(true);
        try {
            // Persiste no backend (tenant.brand.welcomeMessage) — antes isso só
            // atualizava o estado local e o checklist da Home não reconhecia.
            await api.patch("/tenant/settings", { brand: { welcomeMessage: trimmed } });
            updateBrand({ welcomeMessage: trimmed });
            toast.success("Mensagem de boas-vindas salva", {
                description: "Será enviada automaticamente ao cadastrar um novo cliente.",
            });
            // Se veio do checklist "Comece com o pé direito", volta pra Home.
            returnHomeIfRequested(() => setActiveModule("home"));
        } catch (e) {
            toast.error("Não conseguimos salvar a mensagem", {
                description: e?.message || "Tente novamente em instantes.",
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="marketing-welcome-tab" className="grid lg:grid-cols-5 gap-4">
            {/* Editor */}
            <section className="lg:col-span-3 rounded-2xl border border-amber-500/20 bg-slate-900/40 backdrop-blur-xl p-5 space-y-4">
                <header className="flex items-center gap-3 pb-3 border-b border-amber-500/15">
                    <div className="h-10 w-10 rounded-xl bg-amber-500/15 border border-amber-500/40 text-amber-300 flex items-center justify-center">
                        <i className="fa-solid fa-heart"></i>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 font-bold">
                            Mensagem automática de boas-vindas
                        </div>
                        <h3 className="salon-heading text-lg text-white mt-0.5">
                            Primeira impressão importa
                        </h3>
                    </div>
                </header>
                <p className="text-xs text-slate-400 leading-relaxed">
                    Este texto é enviado por WhatsApp automaticamente sempre que um novo cliente é cadastrado. Use os atalhos abaixo para personalizar dinamicamente.
                </p>

                <div>
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-amber-200/70 mb-2 font-bold">
                        Texto da mensagem
                    </label>
                    <textarea
                        data-testid="welcome-msg-textarea"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={7}
                        className="w-full rounded-xl bg-black/60 border border-amber-500/20 px-4 py-3 text-sm text-white outline-none focus:border-amber-400/60 leading-relaxed"
                        placeholder="Escreva a mensagem que seu cliente receberá no primeiro contato…"
                    />
                    <div className="text-[10px] text-slate-500 mt-1.5 flex justify-between">
                        <span>Atalhos disponíveis:</span>
                        <span className="font-mono">{message.length} chars</span>
                    </div>
                </div>

                <div className="flex flex-wrap gap-2">
                    <TokenBtn onClick={() => insertToken("{{cliente_nome}}")} label="Nome do cliente" />
                    <TokenBtn onClick={() => insertToken("{{salao_nome}}")} label="Nome do salão" />
                </div>

                <div className="flex items-center justify-end gap-2 pt-2 border-t border-amber-500/15">
                    <button
                        data-testid="welcome-msg-save"
                        onClick={handleSave}
                        disabled={saving}
                        className="min-h-[44px] px-5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 text-[11px] uppercase tracking-[0.15em] font-bold transition-all shadow-lg shadow-amber-500/20 flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        <i className={`fa-solid ${saving ? "fa-spinner fa-spin" : "fa-floppy-disk"}`}></i>
                        {saving ? "Salvando…" : "Salvar mensagem"}
                    </button>
                </div>
            </section>

            {/* WhatsApp preview */}
            <section className="lg:col-span-2 rounded-2xl border border-emerald-500/20 bg-slate-900/40 backdrop-blur-xl p-5">
                <header className="flex items-center gap-3 pb-3 border-b border-emerald-500/15">
                    <div className="h-10 w-10 rounded-xl bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 flex items-center justify-center">
                        <i className="fa-brands fa-whatsapp text-lg"></i>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 font-bold">
                            Preview
                        </div>
                        <h3 className="salon-heading text-lg text-white mt-0.5">
                            Como o cliente vê
                        </h3>
                    </div>
                </header>
                <div className="mt-4 rounded-xl bg-[#0f1a14] border border-emerald-500/10 p-3">
                    <div className="flex items-center gap-2 pb-2 border-b border-white/5 mb-3">
                        {brand.logoUrl ? (
                            <img src={brand.logoUrl} alt="" className="h-8 w-8 rounded-full object-cover" />
                        ) : (
                            <div className="h-8 w-8 rounded-full bg-amber-500/20 text-amber-300 flex items-center justify-center text-xs">
                                <i className="fa-solid fa-crown"></i>
                            </div>
                        )}
                        <div className="min-w-0">
                            <div className="text-sm font-bold text-white truncate">{brand.shopName || "Kinng.app"}</div>
                            <div className="text-[10px] text-emerald-300/70">online agora</div>
                        </div>
                    </div>
                    <div className="rounded-lg bg-emerald-500/15 border border-emerald-500/25 px-3 py-2.5 text-sm text-emerald-50 whitespace-pre-wrap leading-relaxed" data-testid="welcome-msg-preview">
                        {preview}
                    </div>
                    <div className="text-[9px] text-slate-500 mt-1.5 text-right">
                        {new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })} ✓✓
                    </div>
                </div>
            </section>
        </div>
    );
};

const TokenBtn = ({ label, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        className="min-h-[36px] px-3 rounded-lg border border-amber-500/25 hover:border-amber-400 bg-amber-500/5 hover:bg-amber-500/10 text-amber-200 text-[10px] uppercase tracking-wider font-bold transition-all flex items-center gap-1.5"
    >
        <i className="fa-solid fa-plus text-[9px]"></i>
        {label}
    </button>
);


// ─── TAB 1: VISÃO GERAL ───────────────────────────────────────────────────
const OverviewTab = () => {
    const { campaigns, messageLog, clients, templates, brand, appointments, toggleCampaign, logMessage } = useSalon();

    const kpis = useMemo(() => {
        const totalSent = messageLog.length;
        const totalConverted = messageLog.filter((m) => m.convertedAt).length;
        const conversionRate = totalSent > 0 ? (totalConverted / totalSent) * 100 : 0;
        const npsLogs = messageLog.filter((m) => m.feedback?.rating);
        const npsAvg =
            npsLogs.length > 0
                ? npsLogs.reduce((s, m) => s + m.feedback.rating, 0) / npsLogs.length
                : 0;
        const reactivated = messageLog.filter(
            (m) =>
                (m.campaignId === "cmp-reativacao-corte" || m.campaignId === "cmp-reativacao-barba") &&
                m.convertedAt
        ).length;
        const couponsRedeemed = messageLog.filter(
            (m) => m.campaignId === "cmp-aniversariantes" && m.convertedAt
        ).length;
        // Estimated revenue from conversions (avg ticket R$80)
        const estimatedRevenue = totalConverted * 80;
        return { totalSent, totalConverted, conversionRate, npsAvg, reactivated, couponsRedeemed, estimatedRevenue };
    }, [messageLog]);

    return (
        <div data-testid="marketing-overview" className="space-y-4">
            {/* KPI grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <KpiTile label="Mensagens enviadas" value={kpis.totalSent} icon="fa-paper-plane" />
                <KpiTile label="Clientes reativados" value={kpis.reactivated} icon="fa-rotate-right" accent="amber" />
                <KpiTile label="Cupons resgatados" value={kpis.couponsRedeemed} icon="fa-cake-candles" accent="rose" />
                <KpiTile label="Taxa de conversão" value={`${kpis.conversionRate.toFixed(0)}%`} icon="fa-arrow-trend-up" accent="emerald" />
                <KpiTile label="NPS médio" value={kpis.npsAvg ? kpis.npsAvg.toFixed(1) : "—"} icon="fa-star" accent="amber" />
                <KpiTile label="ROI estimado" value={`R$ ${kpis.estimatedRevenue.toFixed(0)}`} icon="fa-sack-dollar" accent="emerald" highlight />
            </div>

            {/* Active campaigns */}
            <section className="gold-panel p-5">
                <header className="flex items-center justify-between pb-3 gold-border-bottom">
                    <h3 className="gold-text text-base uppercase">Campanhas Ativas</h3>
                    <span className="text-[10px] uppercase tracking-wider text-amber-200/60">
                        {campaigns.filter((c) => c.active).length} de {campaigns.length}
                    </span>
                </header>
                <div className="space-y-2 mt-4">
                    {campaigns.map((cmp) => {
                        const tpl = templates.find((t) => t.id === cmp.templateId);
                        const eligibles = computeEligibleClients(cmp, clients, appointments, brand);
                        return (
                            <CampaignRow
                                key={cmp.id}
                                campaign={cmp}
                                template={tpl}
                                eligibles={eligibles}
                                onToggle={() => toggleCampaign(cmp.id)}
                                onSendFirst={() => {
                                    if (eligibles.length === 0) {
                                        toast.info("Nenhum cliente elegível agora.");
                                        return;
                                    }
                                    const { client, context } = eligibles[0];
                                    const msg = interpolateTemplate(tpl, client, context, brand);
                                    const link = buildWhatsAppLink(client.phone, msg);
                                    window.open(link, "_blank", "noopener");
                                    logMessage({
                                        campaignId: cmp.id,
                                        clientId: client.id,
                                        channel: "whatsapp",
                                        status: "delivered",
                                    });
                                    toast.success(`Mensagem enviada para ${client.name.split(" ")[0]}`);
                                }}
                            />
                        );
                    })}
                </div>
            </section>

            {/* Recent messages (timeline) */}
            <section className="gold-panel p-5">
                <header className="flex items-center justify-between pb-3 gold-border-bottom">
                    <h3 className="gold-text text-base uppercase">Atividade Recente</h3>
                    <span className="text-[10px] uppercase tracking-wider text-amber-200/60">
                        Últimas {Math.min(messageLog.length, 6)} mensagens
                    </span>
                </header>
                <div className="space-y-2 mt-4 max-h-[280px] overflow-y-auto pr-1">
                    {messageLog.length === 0 && (
                        <p className="text-center text-amber-200/40 text-sm py-8">Nenhuma mensagem enviada ainda.</p>
                    )}
                    {[...messageLog]
                        .sort((a, b) => b.sentAt - a.sentAt)
                        .slice(0, 6)
                        .map((m) => {
                            const cmp = campaigns.find((c) => c.id === m.campaignId);
                            const cli = clients.find((c) => c.id === m.clientId);
                            return (
                                <article
                                    key={m.id}
                                    data-testid={`marketing-log-${m.id}`}
                                    className="rounded-lg border border-amber-500/15 bg-black/40 px-3 py-2.5 flex items-center gap-3"
                                >
                                    <div className="h-8 w-8 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 flex items-center justify-center shrink-0">
                                        <i className="fa-brands fa-whatsapp text-sm"></i>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs text-amber-100 font-semibold truncate">
                                            {cli?.name || m.clientId} · {cmp?.name || "Campanha"}
                                        </div>
                                        <div className="text-[10px] text-amber-300/60 font-mono">
                                            {new Date(m.sentAt).toLocaleString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                                            {m.feedback?.rating && ` · NPS ${m.feedback.rating}/5`}
                                        </div>
                                    </div>
                                    {m.convertedAt ? (
                                        <span className="text-[9px] uppercase tracking-wider px-2 py-1 rounded border border-emerald-500/40 bg-emerald-500/15 text-emerald-200 font-bold">
                                            Convertido
                                        </span>
                                    ) : (
                                        <span className="text-[9px] uppercase tracking-wider px-2 py-1 rounded border border-amber-500/30 text-amber-200/70 font-bold">
                                            Enviado
                                        </span>
                                    )}
                                </article>
                            );
                        })}
                </div>
            </section>
        </div>
    );
};

const CampaignRow = ({ campaign, template, eligibles, onToggle, onSendFirst }) => {
    const meta = TRIGGER_META[campaign.trigger?.type] || {};
    const conversion = campaign.stats?.sent > 0 ? (campaign.stats.converted / campaign.stats.sent) * 100 : 0;
    return (
        <div
            data-testid={`marketing-campaign-${campaign.id}`}
            data-campaign-id={campaign.id}
            className={`rounded-xl border bg-black/45 p-3.5 flex flex-col md:flex-row md:items-center gap-3 transition-all ${
                campaign.active ? "border-amber-500/35" : "border-amber-500/12 opacity-70"
            }`}
        >
            <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-amber-500/15 border border-amber-500/30 text-amber-300 shrink-0">
                    <i className={`fa-solid ${meta.icon || "fa-bullhorn"}`}></i>
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <h4 className="text-sm font-bold text-amber-100 truncate">{campaign.name}</h4>
                        {campaign.active ? (
                            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-emerald-500/35 bg-emerald-500/12 text-emerald-200 font-bold">
                                Ativa
                            </span>
                        ) : (
                            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-zinc-500/35 bg-zinc-500/10 text-zinc-300 font-bold">
                                Pausada
                            </span>
                        )}
                    </div>
                    <p className="text-[10px] text-amber-300/60 mt-0.5 truncate font-mono">
                        {meta.label || "Custom"} · {template?.name || "—"}
                    </p>
                    <div className="text-[10px] text-amber-200/60 mt-1 font-mono">
                        {campaign.stats?.sent || 0} enviadas · {campaign.stats?.converted || 0} convertidas · {conversion.toFixed(0)}% conv · <span className="text-amber-300">{eligibles.length} elegíveis agora</span>
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
                <button
                    data-testid={`marketing-toggle-${campaign.id}`}
                    onClick={onToggle}
                    className={`min-h-[40px] px-3 rounded-lg border text-[10px] uppercase tracking-wider font-bold transition-all ${
                        campaign.active
                            ? "border-amber-500/35 hover:border-amber-400/60 text-amber-200"
                            : "border-emerald-500/40 hover:border-emerald-400/65 text-emerald-200"
                    }`}
                >
                    <i className={`fa-solid ${campaign.active ? "fa-pause" : "fa-play"} mr-1`}></i>
                    {campaign.active ? "Pausar" : "Ativar"}
                </button>
                <button
                    data-testid={`marketing-send-${campaign.id}`}
                    onClick={onSendFirst}
                    disabled={eligibles.length === 0 || !template}
                    className="min-h-[40px] px-3 rounded-lg border border-emerald-500/45 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-200 text-[10px] uppercase tracking-wider font-bold disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                    <i className="fa-brands fa-whatsapp mr-1"></i>
                    Enviar próximo
                </button>
            </div>
        </div>
    );
};

// ─── TAB 2: RÉGUAS & TEMPLATES ────────────────────────────────────────────
const RulesTab = () => {
    const { campaigns, templates, upsertTemplate, deleteTemplate, toggleCampaign } = useSalon();
    const [editingTpl, setEditingTpl] = useState(null);

    return (
        <div data-testid="marketing-rules" className="grid lg:grid-cols-[1.1fr_1fr] gap-4">
            {/* Réguas (campaigns) */}
            <section className="gold-panel p-5">
                <header className="flex items-center justify-between pb-3 gold-border-bottom">
                    <h3 className="gold-text text-base uppercase">Réguas Automáticas</h3>
                    <span className="text-[10px] uppercase tracking-wider text-amber-200/60">
                        Eventos disparam mensagens
                    </span>
                </header>
                <div className="space-y-2 mt-4">
                    {campaigns.map((cmp) => (
                        <RuleRow
                            key={cmp.id}
                            campaign={cmp}
                            template={templates.find((t) => t.id === cmp.templateId)}
                            onToggle={() => toggleCampaign(cmp.id)}
                        />
                    ))}
                </div>
            </section>

            {/* Templates */}
            <section className="gold-panel p-5">
                <header className="flex items-center justify-between pb-3 gold-border-bottom">
                    <h3 className="gold-text text-base uppercase">Templates de Mensagem</h3>
                    <button
                        data-testid="marketing-template-new"
                        onClick={() => setEditingTpl({ id: null, name: "", body: "", channel: "whatsapp" })}
                        className="min-h-[36px] px-3 rounded-lg border border-amber-500/30 hover:border-amber-400/60 text-amber-200 text-[10px] uppercase tracking-wider font-bold transition-all"
                    >
                        <i className="fa-solid fa-plus mr-1"></i>Novo
                    </button>
                </header>
                <div className="space-y-2 mt-4 max-h-[420px] overflow-y-auto pr-1">
                    {templates.map((t) => (
                        <article
                            key={t.id}
                            data-testid={`marketing-template-${t.id}`}
                            className="rounded-xl border border-amber-500/18 bg-black/40 p-3 hover:border-amber-500/40 transition-all"
                        >
                            <header className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                    <h4 className="text-sm font-bold text-amber-100 truncate">{t.name}</h4>
                                    <div className="flex items-center gap-1.5 mt-0.5">
                                        <i className="fa-brands fa-whatsapp text-emerald-400 text-[10px]"></i>
                                        <span className="text-[9px] uppercase tracking-wider text-amber-300/60 font-bold">
                                            WhatsApp
                                        </span>
                                    </div>
                                </div>
                                <div className="flex gap-1 shrink-0">
                                    <button
                                        data-testid={`marketing-template-edit-${t.id}`}
                                        onClick={() => setEditingTpl({ ...t })}
                                        className="h-8 w-8 rounded-lg border border-amber-500/25 hover:border-amber-500/55 text-amber-300 transition-all"
                                    >
                                        <i className="fa-solid fa-pen text-[10px]"></i>
                                    </button>
                                    <button
                                        data-testid={`marketing-template-delete-${t.id}`}
                                        onClick={() => {
                                            if (!window.confirm(`Excluir template "${t.name}"?`)) return;
                                            deleteTemplate(t.id);
                                            toast.success("Template removido");
                                        }}
                                        className="h-8 w-8 rounded-lg border border-red-500/25 hover:border-red-500/55 text-red-300 transition-all"
                                    >
                                        <i className="fa-solid fa-trash text-[10px]"></i>
                                    </button>
                                </div>
                            </header>
                            <p className="text-[11px] text-amber-200/70 mt-2 leading-relaxed line-clamp-2">{t.body}</p>
                            {t.variables?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                    {t.variables.map((v) => (
                                        <span
                                            key={v}
                                            className="text-[9px] font-mono px-1.5 py-0.5 rounded border border-amber-500/20 bg-amber-500/8 text-amber-300/80"
                                        >
                                            {`{${v}}`}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </article>
                    ))}
                </div>
            </section>

            {editingTpl && (
                <TemplateEditor
                    template={editingTpl}
                    onClose={() => setEditingTpl(null)}
                    onSave={(payload) => {
                        upsertTemplate(payload);
                        toast.success(payload.id ? "Template atualizado" : "Template criado");
                        setEditingTpl(null);
                    }}
                />
            )}
        </div>
    );
};

const RuleRow = ({ campaign, template, onToggle }) => {
    const meta = TRIGGER_META[campaign.trigger?.type] || {};
    return (
        <div
            data-testid={`marketing-rule-${campaign.id}`}
            className={`rounded-xl border bg-black/40 p-3 flex items-center gap-3 transition-all ${
                campaign.active ? "border-amber-500/30" : "border-amber-500/10"
            }`}
        >
            <div className={`h-10 w-10 rounded-xl flex items-center justify-center border shrink-0 ${
                campaign.active
                    ? "bg-gradient-to-br from-amber-500/25 to-amber-700/5 border-amber-400/40 text-amber-300"
                    : "bg-black/50 border-amber-500/15 text-amber-200/40"
            }`}>
                <i className={`fa-solid ${meta.icon || "fa-bullhorn"}`}></i>
            </div>
            <div className="min-w-0 flex-1">
                <h4 className="text-sm font-bold text-amber-100 truncate">{campaign.name}</h4>
                <p className="text-[10px] text-amber-300/60 truncate">
                    {meta.desc || ""} · Template: <span className="font-mono">{template?.name || "—"}</span>
                </p>
            </div>
            <button
                data-testid={`marketing-rule-toggle-${campaign.id}`}
                onClick={onToggle}
                className={`relative w-12 h-7 rounded-full transition-colors duration-300 shrink-0 ${
                    campaign.active ? "bg-emerald-500/70" : "bg-zinc-700"
                }`}
            >
                <span
                    className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-md transition-transform duration-300 ${
                        campaign.active ? "translate-x-5" : "translate-x-0.5"
                    }`}
                ></span>
            </button>
        </div>
    );
};

const TemplateEditor = ({ template, onClose, onSave }) => {
    const [draft, setDraft] = useState({
        id: template.id || null,
        name: template.name || "",
        body: template.body || "",
        channel: template.channel || "whatsapp",
        variables: template.variables || [],
    });

    // Extract variables from body
    const detectedVars = useMemo(() => {
        const matches = draft.body.match(/\{(\w+)\}/g) || [];
        return [...new Set(matches.map((m) => m.slice(1, -1)))];
    }, [draft.body]);

    const handleSave = () => {
        if (!draft.name.trim() || !draft.body.trim()) {
            toast.error("Informe nome e corpo da mensagem.");
            return;
        }
        onSave({ ...draft, variables: detectedVars });
    };

    return (
        <div
            data-testid="marketing-template-editor"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="gold-panel max-w-lg w-full p-5 lg:p-6 space-y-4">
                <header className="flex items-start justify-between pb-3 gold-border-bottom">
                    <div>
                        <h3 className="gold-text salon-heading text-lg">
                            {draft.id ? "Editar Template" : "Novo Template"}
                        </h3>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-amber-300/60 mt-0.5">
                            Use {`{cliente_nome}`}, {`{salao_nome}`}, {`{link_agendamento}`}...
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="h-9 w-9 rounded-lg border border-amber-500/25 hover:border-amber-500/55 text-amber-200 transition-all"
                    >
                        <i className="fa-solid fa-xmark"></i>
                    </button>
                </header>

                <label className="block">
                    <span className="text-[10px] uppercase tracking-wider text-amber-300/70 font-bold">Nome</span>
                    <input
                        data-testid="marketing-template-name"
                        value={draft.name}
                        onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                        placeholder="Ex: Reativação 60 dias"
                        className="mt-1.5 w-full bg-black/60 border border-amber-500/22 rounded-xl px-3.5 py-2.5 text-amber-100 text-sm outline-none focus:border-amber-400/55 min-h-[44px]"
                    />
                </label>

                <label className="block">
                    <span className="text-[10px] uppercase tracking-wider text-amber-300/70 font-bold">Mensagem</span>
                    <textarea
                        data-testid="marketing-template-body"
                        value={draft.body}
                        onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                        rows={6}
                        placeholder="Olá {cliente_nome}! Faz tempo que não te vemos no {salao_nome}…"
                        className="mt-1.5 w-full bg-black/60 border border-amber-500/22 rounded-xl px-3.5 py-2.5 text-amber-100 text-sm outline-none focus:border-amber-400/55 resize-none font-mono leading-relaxed"
                    />
                </label>

                {detectedVars.length > 0 && (
                    <div data-testid="marketing-template-vars" className="rounded-xl border border-amber-500/15 bg-black/40 p-3">
                        <span className="text-[10px] uppercase tracking-wider text-amber-300/70 font-bold">
                            Variáveis detectadas
                        </span>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                            {detectedVars.map((v) => (
                                <span
                                    key={v}
                                    className="text-[10px] font-mono px-2 py-0.5 rounded border border-amber-500/25 bg-amber-500/8 text-amber-200"
                                >
                                    {`{${v}}`}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                <footer className="flex items-center justify-end gap-2 pt-2">
                    <button
                        onClick={onClose}
                        className="min-h-[44px] px-4 rounded-xl border border-amber-500/20 hover:border-amber-500/45 text-amber-200/80 text-[11px] uppercase tracking-wider font-bold transition-all"
                    >
                        Cancelar
                    </button>
                    <button
                        data-testid="marketing-template-save"
                        onClick={handleSave}
                        className="gold-pill gold-pill-active min-h-[44px] px-5 rounded-xl text-[11px] font-bold uppercase tracking-[0.15em]"
                    >
                        <i className="fa-solid fa-floppy-disk mr-2"></i>Salvar
                    </button>
                </footer>
            </div>
        </div>
    );
};

// ─── TAB 3: NOVA CAMPANHA (Wizard 3 cliques) ──────────────────────────────
const CampaignWizard = ({ seed, onConsumeSeed }) => {
    const { templates, upsertCampaign, brand, clients, appointments } = useSalon();
    const [step, setStep] = useState(1);
    const [audience, setAudience] = useState(null);
    const [template, setTemplate] = useState(null);
    const [triggerMode, setTriggerMode] = useState("event"); // "event" | "scheduled" | "manual"
    const [name, setName] = useState("");
    const [seededFor, setSeededFor] = useState(null); // client name when wizard was pre-filled

    // Consume incoming seed once
    useEffect(() => {
        if (!seed) return;
        let nextAudience = null;
        let nextTemplate = null;
        if (seed.audienceId) {
            const audPreset = AUDIENCE_PRESETS.find((a) => a.id === seed.audienceId);
            if (audPreset) nextAudience = audPreset;
        }
        if (seed.templateId) {
            const tpl = templates.find((t) => t.id === seed.templateId);
            if (tpl) nextTemplate = tpl;
        }
        if (nextAudience) setAudience(nextAudience);
        if (nextTemplate) setTemplate(nextTemplate);
        if (seed.clientName) {
            setName(`Para ${seed.clientName}`);
            setSeededFor(seed.clientName);
        }
        setTriggerMode("manual"); // user must confirm before sending
        // Jump ahead based on what we already have
        if (nextAudience && nextTemplate) setStep(3);
        else if (nextAudience) setStep(2);
        else setStep(1);
        onConsumeSeed?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seed]);

    const STEPS = [
        { id: 1, label: "Público" },
        { id: 2, label: "Template" },
        { id: 3, label: "Gatilho" },
    ];

    const previewClient = useMemo(() => {
        if (!audience) return null;
        const result = computeEligibleClients({ trigger: audience.trigger }, clients, appointments, brand);
        return result[0] || null;
    }, [audience, clients, appointments, brand]);

    const previewMessage = useMemo(() => {
        if (!template || !previewClient) return "";
        return interpolateTemplate(template, previewClient.client, previewClient.context, brand);
    }, [template, previewClient, brand]);

    const handleSave = () => {
        if (!audience || !template) {
            toast.error("Selecione público e template.");
            return;
        }
        const campaign = {
            name: name.trim() || `${audience.label} · ${template.name}`,
            trigger: audience.trigger,
            templateId: template.id,
            active: triggerMode !== "manual", // auto-activate when scheduled or event-driven
            createdAt: Date.now(),
            stats: { sent: 0, converted: 0 },
        };
        upsertCampaign(campaign);
        toast.success("Campanha criada!", { description: campaign.name });
        // Reset wizard
        setStep(1);
        setAudience(null);
        setTemplate(null);
        setName("");
        setTriggerMode("event");
        setSeededFor(null);
    };

    return (
        <div data-testid="marketing-wizard" className="space-y-4">
            {/* Seed banner — when wizard arrived pre-filled from another module */}
            {seededFor && (
                <div
                    data-testid="wizard-seed-banner"
                    className="rounded-xl border border-emerald-500/40 bg-gradient-to-r from-emerald-500/12 to-transparent px-4 py-3 flex items-center gap-3"
                >
                    <div className="h-9 w-9 rounded-lg bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 flex items-center justify-center shrink-0">
                        <i className="fa-solid fa-wand-magic-sparkles"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300/80 font-bold">
                            Wizard pré-preenchido
                        </div>
                        <p className="text-xs text-emerald-100 mt-0.5">
                            Você veio de uma sugestão de marketing para <span className="font-bold">{seededFor}</span>.
                            Audience e template já selecionados — basta confirmar e enviar.
                        </p>
                    </div>
                    <button
                        data-testid="wizard-seed-clear"
                        onClick={() => {
                            setSeededFor(null);
                            setAudience(null);
                            setTemplate(null);
                            setName("");
                            setStep(1);
                        }}
                        className="shrink-0 min-h-[36px] px-3 rounded-lg border border-emerald-500/30 hover:border-emerald-400/60 text-emerald-200 text-[10px] uppercase tracking-wider font-bold"
                    >
                        Limpar
                    </button>
                </div>
            )}

            {/* Stepper */}
            <div className="flex items-center gap-2 px-1">
                {STEPS.map((s, idx) => {
                    const active = step === s.id;
                    const done = step > s.id;
                    return (
                        <div key={s.id} className="flex items-center flex-1">
                            <div
                                data-testid={`wizard-step-${s.id}`}
                                className={`flex items-center gap-2 px-3 py-2 rounded-xl border transition-all ${
                                    active
                                        ? "border-amber-400/60 bg-amber-500/15 text-amber-100"
                                        : done
                                            ? "border-emerald-500/35 bg-emerald-500/10 text-emerald-200"
                                            : "border-amber-500/15 bg-black/40 text-amber-200/50"
                                }`}
                            >
                                <span className="h-6 w-6 rounded-full flex items-center justify-center font-mono text-[11px] font-bold border border-current">
                                    {done ? <i className="fa-solid fa-check text-[10px]"></i> : s.id}
                                </span>
                                <span className="text-[11px] uppercase tracking-wider font-bold">{s.label}</span>
                            </div>
                            {idx < STEPS.length - 1 && (
                                <div className={`flex-1 h-px ${done ? "bg-emerald-500/40" : "bg-amber-500/15"}`}></div>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="grid lg:grid-cols-[1.4fr_1fr] gap-4">
                {/* Left: step content */}
                <section className="gold-panel p-5 lg:p-6 min-h-[480px]">
                    {step === 1 && (
                        <div data-testid="wizard-step1">
                            <h3 className="gold-text salon-heading text-lg pb-3 gold-border-bottom">
                                1. Quem você quer alcançar?
                            </h3>
                            <p className="text-[11px] text-amber-200/60 mt-2 uppercase tracking-wider">
                                Escolha o público da sua campanha
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                                {AUDIENCE_PRESETS.map((aud) => {
                                    const active = audience?.id === aud.id;
                                    return (
                                        <button
                                            key={aud.id}
                                            data-testid={`wizard-audience-${aud.id}`}
                                            onClick={() => setAudience(aud)}
                                            className={`rounded-xl border p-4 text-left transition-all min-h-[80px] flex items-center gap-3 ${
                                                active
                                                    ? "border-amber-400/60 bg-amber-500/15 shadow-[0_0_22px_-10px_rgba(212,175,55,0.6)]"
                                                    : "border-amber-500/15 bg-black/40 hover:border-amber-500/40"
                                            }`}
                                        >
                                            <div className={`h-11 w-11 rounded-xl flex items-center justify-center bg-amber-500/15 border border-amber-500/30 text-amber-300 shrink-0`}>
                                                <i className={`fa-solid ${aud.icon}`}></i>
                                            </div>
                                            <div className="min-w-0">
                                                <div className="text-sm font-bold text-amber-100 truncate">{aud.label}</div>
                                                <div className="text-[10px] text-amber-300/60 mt-0.5">
                                                    {TRIGGER_META[aud.trigger.type]?.desc}
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="flex justify-end mt-5">
                                <button
                                    data-testid="wizard-next-1"
                                    onClick={() => audience && setStep(2)}
                                    disabled={!audience}
                                    className="gold-pill gold-pill-active min-h-[44px] px-5 rounded-xl text-[11px] font-bold uppercase tracking-[0.15em] disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    Avançar
                                    <i className="fa-solid fa-arrow-right ml-2"></i>
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div data-testid="wizard-step2">
                            <h3 className="gold-text salon-heading text-lg pb-3 gold-border-bottom">
                                2. Qual mensagem você quer enviar?
                            </h3>
                            <p className="text-[11px] text-amber-200/60 mt-2 uppercase tracking-wider">
                                Selecione um template (variáveis serão substituídas)
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4 max-h-[320px] overflow-y-auto pr-1">
                                {templates.map((tpl) => {
                                    const active = template?.id === tpl.id;
                                    return (
                                        <button
                                            key={tpl.id}
                                            data-testid={`wizard-template-${tpl.id}`}
                                            onClick={() => setTemplate(tpl)}
                                            className={`rounded-xl border p-3 text-left transition-all min-h-[90px] ${
                                                active
                                                    ? "border-amber-400/60 bg-amber-500/15 shadow-[0_0_22px_-10px_rgba(212,175,55,0.6)]"
                                                    : "border-amber-500/15 bg-black/40 hover:border-amber-500/40"
                                            }`}
                                        >
                                            <div className="flex items-center gap-2 mb-1">
                                                <i className="fa-brands fa-whatsapp text-emerald-400 text-xs"></i>
                                                <h4 className="text-xs font-bold text-amber-100 truncate">{tpl.name}</h4>
                                            </div>
                                            <p className="text-[10px] text-amber-200/65 leading-relaxed line-clamp-3">
                                                {tpl.body}
                                            </p>
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="flex justify-between mt-5">
                                <button
                                    data-testid="wizard-back-2"
                                    onClick={() => setStep(1)}
                                    className="min-h-[44px] px-4 rounded-xl border border-amber-500/25 hover:border-amber-500/55 text-amber-200 text-[11px] uppercase tracking-wider font-bold"
                                >
                                    <i className="fa-solid fa-arrow-left mr-2"></i>Voltar
                                </button>
                                <button
                                    data-testid="wizard-next-2"
                                    onClick={() => template && setStep(3)}
                                    disabled={!template}
                                    className="gold-pill gold-pill-active min-h-[44px] px-5 rounded-xl text-[11px] font-bold uppercase tracking-[0.15em] disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    Avançar
                                    <i className="fa-solid fa-arrow-right ml-2"></i>
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div data-testid="wizard-step3">
                            <h3 className="gold-text salon-heading text-lg pb-3 gold-border-bottom">
                                3. Quando disparar?
                            </h3>
                            <p className="text-[11px] text-amber-200/60 mt-2 uppercase tracking-wider">
                                Defina o gatilho da campanha
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                                {[
                                    { id: "event", label: "No evento", desc: "Dispara automaticamente quando o critério for atendido", icon: "fa-bolt" },
                                    { id: "scheduled", label: "Agendado", desc: "Dispara todo dia às 10h para novos elegíveis", icon: "fa-clock" },
                                    { id: "manual", label: "Manual", desc: "Você decide quando enviar (cria pausada)", icon: "fa-hand-pointer" },
                                ].map((g) => {
                                    const active = triggerMode === g.id;
                                    return (
                                        <button
                                            key={g.id}
                                            data-testid={`wizard-trigger-${g.id}`}
                                            onClick={() => setTriggerMode(g.id)}
                                            className={`rounded-xl border p-4 text-left transition-all ${
                                                active
                                                    ? "border-amber-400/60 bg-amber-500/15 shadow-[0_0_22px_-10px_rgba(212,175,55,0.6)]"
                                                    : "border-amber-500/15 bg-black/40 hover:border-amber-500/40"
                                            }`}
                                        >
                                            <i className={`fa-solid ${g.icon} text-amber-300 text-lg`}></i>
                                            <h4 className="text-xs font-bold text-amber-100 mt-2 uppercase tracking-wider">{g.label}</h4>
                                            <p className="text-[10px] text-amber-200/60 mt-1">{g.desc}</p>
                                        </button>
                                    );
                                })}
                            </div>
                            <label className="block mt-4">
                                <span className="text-[10px] uppercase tracking-wider text-amber-300/70 font-bold">Nome da campanha (opcional)</span>
                                <input
                                    data-testid="wizard-name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder={audience && template ? `${audience.label} · ${template.name}` : "Nomeie sua campanha"}
                                    className="mt-1.5 w-full bg-black/60 border border-amber-500/22 rounded-xl px-3.5 py-2.5 text-amber-100 text-sm outline-none focus:border-amber-400/55 min-h-[44px]"
                                />
                            </label>
                            <div className="flex justify-between mt-5">
                                <button
                                    data-testid="wizard-back-3"
                                    onClick={() => setStep(2)}
                                    className="min-h-[44px] px-4 rounded-xl border border-amber-500/25 hover:border-amber-500/55 text-amber-200 text-[11px] uppercase tracking-wider font-bold"
                                >
                                    <i className="fa-solid fa-arrow-left mr-2"></i>Voltar
                                </button>
                                <button
                                    data-testid="wizard-save"
                                    onClick={handleSave}
                                    className="gold-pill gold-pill-active min-h-[48px] px-6 rounded-xl text-[11px] font-bold uppercase tracking-[0.15em]"
                                >
                                    <i className="fa-solid fa-rocket mr-2"></i>Criar Campanha
                                </button>
                            </div>
                        </div>
                    )}
                </section>

                {/* Right: Live preview */}
                <aside data-testid="wizard-preview" className="gold-panel p-5 lg:p-6 lg:sticky lg:top-4 self-start">
                    <h4 className="gold-text text-sm uppercase tracking-wider pb-3 gold-border-bottom">
                        <i className="fa-solid fa-eye mr-2"></i>Pré-visualização
                    </h4>
                    <div className="mt-4 space-y-3">
                        <SumLine label="Público" value={audience?.label || "—"} />
                        <SumLine label="Elegíveis agora" value={audience ? `${computeEligibleClients({ trigger: audience.trigger }, clients, appointments, brand).length} clientes` : "—"} mono />
                        <SumLine label="Template" value={template?.name || "—"} />
                        <SumLine label="Disparo" value={triggerMode === "event" ? "Automático no evento" : triggerMode === "scheduled" ? "Diário 10h" : "Manual"} />
                    </div>

                    {previewMessage && (
                        <div className="mt-4">
                            <span className="text-[10px] uppercase tracking-wider text-amber-300/70 font-bold">
                                Como vai chegar:
                            </span>
                            <div className="mt-2 rounded-2xl border border-emerald-500/30 bg-emerald-900/20 p-3 relative">
                                <div className="absolute -top-2 left-3 px-1.5 text-[9px] bg-black uppercase tracking-wider text-emerald-300 font-bold">
                                    WhatsApp
                                </div>
                                <p className="text-xs text-emerald-100/90 leading-relaxed whitespace-pre-wrap">
                                    {previewMessage}
                                </p>
                                <div className="text-[9px] text-emerald-400/60 font-mono mt-2 text-right">
                                    para: {previewClient?.client?.name?.split(" ")[0]}
                                </div>
                            </div>
                        </div>
                    )}
                </aside>
            </div>
        </div>
    );
};

// ─── Shared bits ─────────────────────────────────────────────────────────
const KpiTile = ({ label, value, icon, accent, highlight }) => {
    const colors = {
        amber: "#fde7a8",
        emerald: "#34d399",
        rose: "#fda4af",
        sky: "#7dd3fc",
    };
    const textColor = colors[accent] || "#fde7a8";
    return (
        <div
            className={`gold-panel p-3 ${
                highlight ? "ring-1 ring-emerald-400/40 shadow-[0_0_25px_-12px_rgba(16,185,129,0.5)]" : ""
            }`}
        >
            <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300/70 font-bold truncate">{label}</div>
                <i className={`fa-solid ${icon} text-amber-300/80 text-sm`}></i>
            </div>
            <div
                className="salon-heading text-xl mt-1 font-mono truncate"
                style={{ color: textColor }}
            >
                {value}
            </div>
        </div>
    );
};

const SumLine = ({ label, value, mono }) => (
    <div className="flex items-center justify-between gap-2 py-1.5 border-b border-amber-500/8 last:border-b-0">
        <span className="text-[10px] uppercase tracking-wider text-amber-300/70 font-bold">{label}</span>
        <span className={`truncate text-amber-100 text-sm ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
);

export default Marketing;
